import { Alert, Box, Button, Typography } from "@mui/material";
import React, { Component, ErrorInfo, ReactNode } from "react";
import { ANALYTICS_ERROR_CODES, analyticsService } from "../services/AnalyticsService";
import { createLogger } from "../utils/logger";

const logger = createLogger("ErrorBoundary");

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  componentName?: string;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { componentName = "Unknown" } = this.props;

    // Log error to console for development
    logger.error(`Error caught in ${componentName}:`, error, errorInfo);

    // Track error in analytics
    analyticsService.logError(ANALYTICS_ERROR_CODES.COMPONENT_ERROR, error.message, {
      errorStack: error.stack,
      component: componentName,
      userAction: "component_error",
      severity: "high",
    });

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { fallback, componentName = "Component" } = this.props;

      if (fallback) {
        return fallback;
      }

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            p: 3,
            textAlign: "center",
          }}
        >
          <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
            <Typography variant="h6" gutterBottom>
              Something went wrong in {componentName}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              We&apos;ve been notified about this error and are working to fix it.
            </Typography>
            {process.env.NODE_ENV === "development" && this.state.error && (
              <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Typography variant="caption" component="pre" sx={{ fontSize: "0.75rem" }}>
                  {this.state.error.message}
                </Typography>
              </Box>
            )}
          </Alert>

          <Box sx={{ display: "flex", gap: 2 }}>
            <Button variant="outlined" onClick={this.handleRetry}>
              Try Again
            </Button>
            <Button variant="contained" onClick={this.handleReload}>
              Reload Page
            </Button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easy wrapping
export const withErrorBoundary = <P extends object>(Component: React.ComponentType<P>, componentName?: string) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary componentName={componentName}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  return WrappedComponent;
};

export default ErrorBoundary;
